import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInsuranceDropdownOpen, setIsInsuranceDropdownOpen] = useState(false);
  const location = useLocation();

  const insuranceProducts = [
    { name: 'Small Business Insurance', href: '/small-business-insurance' },
    { name: 'Business Owners Policy', href: '/business-owners-policy' },
    { name: 'General Liability', href: '/general-liability' },
    { name: 'Professional Liability', href: '/professional-liability' },
    { name: 'Cyber Insurance', href: '/cyber-insurance' },
    { name: 'Workplace Violence', href: '/workplace-violence' },
    { name: 'Workers Compensation', href: '/workers-compensation' }
  ];

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Insurance Products', href: '#', dropdown: insuranceProducts },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Benefits Bar */}
      <div className="bg-blue-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center text-sm">
            <div className="flex items-center space-x-8">
              <span>✓ No Agents Needed</span>
              <span>✓ Instant Quotes</span>
              <span>✓ Same Day Coverage</span>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-center">
          <Link to="/" className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://app1.sharemyimage.com/2025/05/19/burbank-insurance-services-in-california-logo.png"
                alt="Burbank Insurance"
                className="h-12"
              />
              <span className="text-2xl font-bold text-blue-600">+</span>
              <img
                src="https://cdn1.sharemyimage.com/smi/2025/09/11/Coterie_Logo_Trans_FullColor-1024x185.png"
                alt="Coterie Insurance"
                className="h-8"
              />
            </div>
          </Link>
        </div>
      </div>

      {/* Navigation Section */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 mx-auto">
              {navigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.dropdown ? (
                    <div
                      className="relative"
                      onMouseEnter={() => setIsInsuranceDropdownOpen(true)}
                      onMouseLeave={() => setIsInsuranceDropdownOpen(false)}
                    >
                      <button className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
                        {item.name}
                        <SafeIcon icon={FiChevronDown} className="ml-1 h-4 w-4" />
                      </button>
                      {isInsuranceDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 -mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                          style={{ paddingTop: '8px' }}
                        >
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors border-b border-gray-100 last:border-b-0"
                              onClick={() => setIsInsuranceDropdownOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors ${
                        location.pathname === item.href ? 'text-blue-600 border-b-2 border-blue-600' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors shadow-lg"
              >
                Get My Price
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-gray-700 hover:text-blue-600 ml-auto"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden pb-4"
            >
              <div className="space-y-2">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.dropdown ? (
                      <div>
                        <div className="px-3 py-2 text-base font-medium text-gray-700">
                          {item.name}
                        </div>
                        <div className="pl-6 space-y-1">
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              onClick={() => setIsMenuOpen(false)}
                              className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md ${
                          location.pathname === item.href ? 'text-blue-600 bg-blue-50' : ''
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                <a
                  href="https://coterie.com/quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors mt-4"
                >
                  Get My Price
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;